import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  AccountCircle,
  Notifications,
  Security,
  Support,
  LocalShipping,
  Business,
  Refresh,
  Email,
  Phone,
  LocationOn,
  Work,
  CalendarToday,
  CreditCard,
  Settings
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { quikqAuthService } from '../services/quikqAuth';
import { quikqApiService, QuikQUserProfile, CarrierInfo, DriverInfo, CardInfo } from '../services/quikqApi';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState<QuikQUserProfile | null>(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [carrierInfo, setCarrierInfo] = useState<CarrierInfo | null>(null);
  const [driverInfo, setDriverInfo] = useState<DriverInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rawApiData, setRawApiData] = useState<any>(null);
  const [cards, setCards] = useState<CardInfo[]>([]);
  const [drivers, setDrivers] = useState<DriverInfo[]>([]);

  // Get user role from auth service
  const getUserRole = (): 'DRIVER' | 'CARRIER' => {
    const role = quikqAuthService.getCurrentUserRole();
    // console.log('🔍 Role from auth service:', role);
    
    // Map QuikQ roles to our simplified role system
    if (role === 'DRIVER') {
      return 'DRIVER';
    }
    // Map ADMIN and CARRIER to CARRIER (ADMIN functionality is merged into CARRIER)
    if (role === 'ADMIN' || role === 'CARRIER') {
      return 'CARRIER';
    }
    
    // Default to CARRIER if role is not recognized or null
    return 'CARRIER';
  };
  const userRole = getUserRole();

  // Fetch user profile data
  const fetchUserProfile = async () => {
    setLoading(true);
    setError('');
    try {
      const currentUser = quikqAuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      // console.log('🔍 Fetching profile for user:', currentUser.userId, 'with role:', userRole);

      // Get user profile - using basic user data for now
      const profile: QuikQUserProfile = {
        userId: currentUser.userId,
        username: currentUser.userId,
        email: currentUser.userId,
        firstName: 'User',
        lastName: 'Profile',
        startCode: currentUser.startCode || '',
        role: currentUser.role || 'DRIVER',
        isActive: true,
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
        companyName: 'NorthStar Fuel',
        status: 'ACTIVE',
      };
      setUserProfile(profile);
      // console.log('🔍 User profile data:', profile);

      // Get additional data based on role
      if (userRole === 'DRIVER' && currentUser.startCode) {
        try {
          // Get carrier info for driver
          await fetchCarrierInfo(currentUser.startCode);
        } catch (err) {
          // console.log('⚠️ Could not fetch carrier info for driver:', err);
        }
      } else if (userRole === 'CARRIER' && currentUser.startCode) {
        try {
          // Get drivers list for carrier
          await fetchCarrierDrivers(currentUser.startCode);
          
          // Get carrier info
          await fetchCarrierInfo(currentUser.startCode);
        } catch (err) {
          // console.log('⚠️ Could not fetch carrier data:', err);
        }
      }

      // Fetch cards for all users
      if (currentUser.startCode) {
        try {
          await fetchCards(currentUser.startCode);
        } catch (err) {
          // console.log('⚠️ Could not fetch cards:', err);
        }
      }

    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError(err instanceof Error ? err.message : 'Unable to load profile data');
      
      // Set basic user data from auth service as fallback
      const currentUser = quikqAuthService.getCurrentUser();
      if (currentUser) {
        setUserProfile({
          userId: currentUser.userId,
          username: currentUser.userId,
          email: currentUser.userId,
          firstName: 'User',
          lastName: 'Profile',
          startCode: currentUser.startCode || '',
          role: currentUser.role || 'DRIVER',
          isActive: true,
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
          companyName: 'NorthStar Fuel',
          status: 'ACTIVE',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch cards data
  const fetchCards = async (startCode: string) => {
    try {
      const cardsData = await quikqApiService.getCarrierCards(startCode);
      // console.log('🔍 Cards data:', cardsData);
      setCards(cardsData);
    } catch (error) {
      console.error('Error fetching cards:', error);
      setCards([]);
    }
  };

  // Fetch carrier info
  const fetchCarrierInfo = async (startCode: string) => {
    try {
      const carrierData = await quikqApiService.getCarrierInfo(startCode);
      // console.log('🔍 Carrier info:', carrierData);
      setCarrierInfo(carrierData);
    } catch (error) {
      console.error('Error fetching carrier info:', error);
    }
  };

  // Fetch carrier drivers
  const fetchCarrierDrivers = async (startCode: string) => {
    try {
      const driversData = await quikqApiService.getCarrierDrivers(startCode);
      // console.log('🔍 Carrier drivers:', driversData);
      setDrivers(driversData);
    } catch (error) {
      console.error('Error fetching carrier drivers:', error);
      setDrivers([]);
    }
  };

  // Fetch raw API data for debugging
  const fetchRawApiData = async () => {
    try {
      setLoading(true);
      setError('');

      const currentUser = quikqAuthService.getCurrentUser();
      if (!currentUser) {
        setError('No user found. Please log in again.');
        return;
      }

      // console.log('🔍 Current user:', currentUser);
      // console.log('🔍 User role:', currentUser.role);
      // console.log('🔍 Start code:', currentUser.startCode);

      const authHeaders = await quikqAuthService.getAuthHeaders();
      
      // Try multiple endpoints to see what data we can get
      const endpoints = [
        { name: 'carrier', url: `http://localhost:3001/api/carriers/${currentUser.startCode}` },
        { name: 'drivers', url: `http://localhost:3001/api/carriers/${currentUser.startCode}/drivers` },
        { name: 'cards', url: `http://localhost:3001/api/carriers/${currentUser.startCode}/cards` },
        { name: 'savings', url: `http://localhost:3001/api/reports/savings/${currentUser.startCode}` },
        { name: 'transactions', url: `http://localhost:3001/api/reports/transactions/${currentUser.startCode}` },
        { name: 'stations', url: `http://localhost:3001/api/carriers/stations` },
        { name: 'stationsDetail', url: `http://localhost:3001/api/carriers/stationsDetail` },
        { name: 'user_info', url: `http://localhost:3001/api/user/info` },
        { name: 'account', url: `http://localhost:3001/api/account` },
        { name: 'profile', url: `http://localhost:3001/api/profile` },
      ];

      const results: any = {};

      for (const endpoint of endpoints) {
        try {
          // console.log(`🔍 Trying endpoint: ${endpoint.name} (${endpoint.url})`);
          const response = await fetch(endpoint.url, {
            method: 'GET',
            headers: authHeaders,
          });

          if (response.ok) {
            const data = await response.json();
            results[endpoint.name] = {
              status: response.status,
              data: data
            };
            // console.log(`✅ ${endpoint.name} success:`, data);
          } else {
            results[endpoint.name] = {
              status: response.status,
              error: `${response.status} ${response.statusText}`
            };
            // console.log(`❌ ${endpoint.name} failed: ${response.status} ${response.statusText}`);
          }
        } catch (err) {
          results[endpoint.name] = {
            error: err instanceof Error ? err.message : 'Network error'
          };
          // console.log(`❌ ${endpoint.name} error:`, err);
        }
      }

      setRawApiData(results);
      // console.log('🔍 All API results:', results);
    } catch (err) {
      console.error('Error fetching raw API data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch raw API data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };

  const confirmLogout = () => {
    quikqAuthService.logout();
    navigate('/login');
  };

  const cancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 3, md: 4 }, 
      pb: { xs: 12, sm: 12, md: 12 }, 
      bgcolor: '#f5f6fa', 
      minHeight: '100vh',
      position: 'relative',
      zIndex: 0,
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '300px',
        height: '300px',
        backgroundImage: 'url(/NSFIcon.png)',
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        opacity: 0.03,
        zIndex: 0,
        pointerEvents: 'none'
      },
      '& > *': {
        position: 'relative',
        zIndex: 1
      }
    }}>
      {/* Header with Split Watermark - Left: NSFLogo, Right: Empty (for future custom logo) */}
      <Box sx={{ 
        position: 'relative', 
        mb: 3,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -50,
          left: -50,
          width: '100%',
          height: '200px',
          background: `url('/NSFIcon.png')`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          opacity: 0.08,
          zIndex: 0,
          pointerEvents: 'none'
        }
      }}></Box>

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ mt: { xs: 2, sm: 3, md: 4 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
            {error}
          </Alert>
        )}



        {/* Modern User Profile Card */}
        {userProfile && (
          <Card sx={{ 
            mb: { xs: 2, sm: 3, md: 4 },
            borderRadius: 4,
            overflow: 'visible',
            boxShadow: 3,
            position: 'relative'
          }}>
            {/* Blue Header Background */}
            <Box sx={{ 
              height: { xs: 80, sm: 100, md: 120 },
              bgcolor: 'primary.main',
              borderRadius: '16px 16px 0 0',
              position: 'relative'
            }} />
            
            {/* Profile Icon - Centered and Overlapping */}
            <Box sx={{ 
              position: 'absolute',
              top: { xs: 40, sm: 50, md: 60 },
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2
            }}>
              <Box sx={{
                width: { xs: 80, sm: 100, md: 120 },
                height: { xs: 80, sm: 100, md: 120 },
                borderRadius: '50%',
                bgcolor: 'white',
                border: `4px solid ${userProfile.status === 'ACTIVE' ? '#4caf50' : '#f44336'}`,
                boxShadow: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}>
                <img 
                  src="/NSFIcon.png" 
                  alt="NSF Icon"
                  style={{
                    width: '70%',
                    height: '70%',
                    objectFit: 'contain'
                  }}
                />
              </Box>
            </Box>
            
            {/* Main Content */}
            <CardContent sx={{ 
              pt: { xs: 8, sm: 10, md: 12 },
              pb: { xs: 3, sm: 4, md: 5 },
              px: { xs: 3, sm: 4, md: 5 },
              textAlign: 'center'
            }}>
              {/* User Name */}
              <Typography variant="h5" gutterBottom sx={{ 
                fontSize: { xs: 20, sm: 22, md: 24 },
                fontWeight: 'bold',
                color: 'text.primary',
                mb: 1
              }}>
                {userProfile.firstName} {userProfile.lastName}
              </Typography>
              
              {/* Company Name */}
              <Typography variant="body1" color="text.secondary" sx={{ 
                fontSize: { xs: 14, sm: 16, md: 18 },
                mb: 3
              }}>
                {userProfile.companyName || 'NorthStar Fuel'}
              </Typography>
              

              
              {/* Info Section */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 2,
                alignItems: 'center'
              }}>
                {/* Email */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  minWidth: { xs: '100%', sm: 300 }
                }}>
                  <Email sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ 
                    fontSize: { xs: 14, sm: 16 },
                    fontWeight: 500
                  }}>
                    {userProfile.email}
                  </Typography>
                </Box>
                
                {/* Start Code */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  minWidth: { xs: '100%', sm: 300 }
                }}>
                  <Work sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ 
                    fontSize: { xs: 14, sm: 16 },
                    fontWeight: 500
                  }}>
                    Start Code: {userProfile.startCode}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Role-specific Information */}
        {userRole === 'DRIVER' && carrierInfo && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Carrier Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="textSecondary">Carrier Name</Typography>
                  <Typography variant="body1" fontWeight={500}>{carrierInfo.carrierName}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">Status</Typography>
                  <Chip 
                    label={carrierInfo.isActive ? 'ACTIVE' : 'INACTIVE'} 
                    color={carrierInfo.isActive ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                  <Typography variant="body2" color="textSecondary">Address</Typography>
                  <Typography variant="body1">
                    {carrierInfo.address}, {carrierInfo.city}, {carrierInfo.state} {carrierInfo.zip}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">Phone</Typography>
                  <Typography variant="body1">{carrierInfo.phone}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">Email</Typography>
                  <Typography variant="body1">{carrierInfo.email}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {userRole === 'CARRIER' && carrierInfo && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Company Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="textSecondary">Company Name</Typography>
                  <Typography variant="body1" fontWeight={500}>{carrierInfo.carrierName}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">Status</Typography>
                  <Chip 
                    label={carrierInfo.isActive ? 'ACTIVE' : 'INACTIVE'} 
                    color={carrierInfo.isActive ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                  <Typography variant="body2" color="textSecondary">Address</Typography>
                  <Typography variant="body1">
                    {carrierInfo.address}, {carrierInfo.city}, {carrierInfo.state} {carrierInfo.zip}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">Phone</Typography>
                  <Typography variant="body1">{carrierInfo.phone}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">Email</Typography>
                  <Typography variant="body1">{carrierInfo.email}</Typography>
                </Box>
                {carrierInfo.remainingCredit !== undefined && (
                  <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                    <Typography variant="body2" color="textSecondary">Remaining Credit</Typography>
                    <Typography variant="body1" color="primary" fontWeight={500}>
                      ${carrierInfo.remainingCredit.toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        )}





        {/* Debug Section
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Debug Info
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchRawApiData}
              sx={{ mr: 1 }}
            >
              Fetch Raw API Data
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchUserProfile}
            >
              Refresh Profile
            </Button>
          </Box>
          
          {rawApiData && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                API Endpoint Results:
              </Typography>
              {Object.entries(rawApiData).map(([endpointName, result]: [string, any]) => (
                <Box key={endpointName} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    {endpointName.toUpperCase()}
                  </Typography>
                  {result.status && (
                    <Typography variant="body2" color={result.status === 200 ? 'success.main' : 'error.main'}>
                      Status: {result.status}
                    </Typography>
                  )}
                  {result.error && (
                    <Typography variant="body2" color="error.main">
                      Error: {result.error}
                    </Typography>
                  )}
                  {result.data && (
                    <Box sx={{ 
                      bgcolor: '#f5f5f5', 
                      p: 1, 
                      borderRadius: 1, 
                      mt: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.7rem',
                      maxHeight: 200,
                      overflow: 'auto'
                    }}>
                      <pre>{JSON.stringify(result.data, null, 2)}</pre>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Paper> */}

        {/* Navigation Menu */}
        <Paper sx={{ mt: 3 }}>
          <List>
            {/* Account Details temporarily removed */}
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate('/cards')}>
                <ListItemIcon>
                  <CreditCard />
                </ListItemIcon>
                <ListItemText 
                  primary="Cards Information" 
                  secondary={cards.length === 0 ? 'No cards available' : `${cards.length} card${cards.length === 1 ? '' : 's'} available`}
                />
              </ListItemButton>
            </ListItem>
            {userRole === 'CARRIER' && (
              <>
                <Divider />
                <ListItem disablePadding>
                  <ListItemButton onClick={() => navigate('/drivers')}>
                    <ListItemIcon>
                      <LocalShipping />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Drivers Information" 
                      secondary={drivers.length === 0 ? 'No drivers assigned to this carrier' : `${drivers.length} driver${drivers.length === 1 ? '' : 's'} assigned`}
                    />
                  </ListItemButton>
                </ListItem>
              </>
            )}
            <Divider />
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate('/profile/notifications')}>
                <ListItemIcon>
                  <Notifications />
                </ListItemIcon>
                <ListItemText 
                  primary="Notifications" 
                  secondary={userRole === 'DRIVER' ? 'Driver alerts and updates' : 'Carrier alerts and system notifications'}
                />
              </ListItemButton>
            </ListItem>
            <Divider />
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate('/profile/settings')}>
                <ListItemIcon>
                  <Settings />
                </ListItemIcon>
                <ListItemText primary="Settings" secondary="Manage your preferences and stay logged in duration" />
              </ListItemButton>
            </ListItem>
            <Divider />
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate('/profile/support')}>
                <ListItemIcon>
                  <Support />
                </ListItemIcon>
                <ListItemText primary="Support" secondary="Get help and contact support" />
              </ListItemButton>
            </ListItem>
            <Divider />
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon>
                  <Security />
                </ListItemIcon>
                <ListItemText primary="Logout" secondary="Sign out of your account" />
              </ListItemButton>
            </ListItem>
          </List>
        </Paper>
        </Box>
      </Container>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialogOpen} onClose={cancelLogout}>
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to logout?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelLogout} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmLogout} color="error" variant="contained">
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile; 